/**
 * 실행계획 옆의 **증상 · 비슷한 사례 · 운영 실행 이력.**
 *
 * 수작업으로 실행계획을 뽑았을 때 지금까지는 계획을 그리고 끝이었다. 참고할 것도, 이 쿼리가
 * 운영에서 어떻게 돌았는지도 없었다(docs/features/14-tuning-workbench.md).
 *
 * ## 증상은 서버가 뽑는다
 *
 * 화면이 직접 판단하지 않는다. 에이전트도 같은 판단을 하는데 두 벌로 두면 "화면이 말하는
 * 증상" 과 "에이전트가 말하는 증상" 이 달라지고, 그때 사용자는 어느 쪽을 믿어야 할지 모른다.
 * 사례 열람 경계(티어)도 저장소 한 곳이 판정해야 하므로 검색까지 서버가 끝낸다.
 *
 * ## 없는 것은 그리지 않는다
 *
 * 증상이 없거나 걸린 사례가 없으면 그 영역을 **아예 그리지 않는다.** 빈 상자는 "이 기능이
 * 고장 났나" 로 읽힌다. 다만 **못 읽은 것**(권한 없음)은 다르다 — 그건 말해 준다.
 *
 * ## 지문은 추측하지 않는다
 *
 * 편집기 텍스트만으로는 이 쿼리가 운영의 어느 sql_id 인지 알 수 없다. 후보가 여럿이면
 * 사람이 고른다 — 자동으로 붙이면 남의 쿼리 이력을 이 쿼리 것이라고 말하게 된다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronRight, FiClock, FiAlertTriangle } from 'react-icons/fi';
import * as api from '../../services/assistantClient';
import styles from './PlanInsights.module.css';

/** 이력 소스가 왜 비었는지 — 서버가 준 이유 코드를 문구로. */
const REASON_KEY = {
    NO_PRIVILEGE: 'noPrivilege',
    UNSUPPORTED: 'unsupported',
    NOT_COLLECTED: 'notCollected',
    NOT_GRANTED: 'notGranted',
    ERROR: 'error',
};

function PlanInsights({ planText, connectionId, sqlText }) {
    const { t } = useTranslation();
    // 증상·이력은 **기본 접힘**이다. 실행계획을 보러 온 사람에게 옆칸이 먼저 자리를 차지하면
    // 정작 계획이 밀린다 — 필요할 때 펴 보게 한다. 사례는 펴 둔다(가장 바로 쓸 수 있는 정보).
    const [openSymptoms, setOpenSymptoms] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);
    const [insights, setInsights] = useState(null);
    const [openCase, setOpenCase] = useState(null);
    const [caseBody, setCaseBody] = useState({});

    const [history, setHistory] = useState(null);
    const [candidates, setCandidates] = useState(null);
    const [histBusy, setHistBusy] = useState(false);
    const [histError, setHistError] = useState(null);

    // 계획이 바뀌면 전부 다시 — 앞 계획의 사례가 남아 있으면 엉뚱한 것을 참고하게 된다
    useEffect(() => {
        setInsights(null); setOpenCase(null); setCaseBody({});
        setHistory(null); setCandidates(null); setHistError(null);
        setOpenSymptoms(false); setOpenHistory(false);
        if (!planText || planText.length < 20) return undefined;
        let alive = true;
        api.getPlanInsights(planText)
            .then(r => { if (alive) setInsights(r); })
            .catch(() => { if (alive) setInsights(null); });   // 부가 정보라 실패해도 계획은 그대로 보여 준다
        return () => { alive = false; };
    }, [planText]);

    const toggleCase = useCallback(async (id) => {
        if (openCase === id) { setOpenCase(null); return; }
        setOpenCase(id);
        if (caseBody[id] !== undefined) return;
        try {
            // getTuningCase 는 사례를 **그대로** 준다 (r.case 가 아니다)
            const c = await api.getTuningCase(id);
            setCaseBody(prev => ({ ...prev, [id]: c || null }));
        } catch (_e) {
            setCaseBody(prev => ({ ...prev, [id]: null }));
        }
    }, [openCase, caseBody]);

    const loadHistory = useCallback(async (sqlId) => {
        setHistBusy(true);
        setHistError(null);
        try {
            if (!sqlId) {
                const f = await api.resolveFingerprint({ connectionId, sqlText });
                if (!f.confirmed) {
                    setCandidates(f.candidates || []);
                    if (!f.candidates || !f.candidates.length) setHistError(t(`assistant:planInsights.fp.${f.reason || 'NOT_FOUND'}`, t('assistant:planInsights.fp.NOT_FOUND')));
                    return;
                }
                sqlId = f.confirmed.sqlId;
            }
            setCandidates(null);
            setHistory(await api.getSqlHistory({ connectionId, sqlId }));
        } catch (err) {
            setHistError(err.message);
        } finally { setHistBusy(false); }
    }, [connectionId, sqlText, t]);

    const symptoms = (insights && insights.symptoms) || [];
    const cases = (insights && insights.cases) || [];
    const canAskHistory = !!(connectionId && sqlText && sqlText.trim().length >= 20);

    // 보여 줄 것이 하나도 없으면 자리를 차지하지 않는다
    if (!symptoms.length && !cases.length && !canAskHistory) return null;

    return (
        <div className={styles.wrap}>
            {symptoms.length > 0 && (
                <div className={styles.block}>
                    <button type="button" className={styles.sectionToggle} onClick={() => setOpenSymptoms(o => !o)}>
                        {openSymptoms ? <FiChevronDown size={10} /> : <FiChevronRight size={10} />}
                        <span className={styles.title}>{t('assistant:planInsights.symptoms')}</span>
                        <span className={styles.count}>{symptoms.length}</span>
                    </button>
                    {openSymptoms && (
                        <div className={styles.badges}>
                            {symptoms.map(s => (
                                // 증상 이름은 id 로 번역하고(서버 라벨은 한국어 고정), 무엇을 뜻하는지와 근거를 툴팁에 같이 둔다 —
                                // 「카디널리티 오차」 가 무슨 뜻이냐는 질문이 있었다(2026-10-07)
                                <span key={s.id} className={styles.badge}
                                    title={[t(`assistant:planInsights.symHelp.${s.id}`, { defaultValue: '' }), s.why ? t('assistant:planInsights.symWhere', { why: s.why }) : ''].filter(Boolean).join(' — ')}>
                                    <FiAlertTriangle size={9} /> {t(`assistant:planInsights.sym.${s.id}`, { defaultValue: s.label })}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {cases.length > 0 && (
                <div className={styles.block}>
                    <div className={styles.title}>{t('assistant:planInsights.cases')}</div>
                    {cases.map(c => (
                        <div key={c.id} className={styles.caseItem}>
                            <button type="button" className={styles.caseHead} onClick={() => toggleCase(c.id)}>
                                {openCase === c.id ? <FiChevronDown size={10} /> : <FiChevronRight size={10} />}
                                <span className={styles.caseTitle}>{c.title}</span>
                            </button>
                            {(c.symptom || c.improvement) && (
                                <div className={styles.caseMeta}>
                                    {c.symptom}{c.symptom && c.improvement ? ' · ' : ''}{c.improvement}
                                </div>
                            )}
                            {openCase === c.id && (
                                <div className={styles.caseBody}>
                                    {caseBody[c.id] === undefined
                                        ? t('assistant:planInsights.loading')
                                        : (caseBody[c.id] ? caseBody[c.id].body : t('assistant:planInsights.caseGone'))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* 사례가 안 걸린 이유가 **부여가 없어서**라면 그건 말해 준다 (빈 것과 다르다) */}
            {symptoms.length > 0 && !cases.length && insights && !insights.sharedCorpus && (
                <div className={styles.note}>{t('assistant:planInsights.corpusLocked')}</div>
            )}

            {canAskHistory && (
                <div className={styles.block}>
                    <button type="button" className={styles.sectionToggle} onClick={() => setOpenHistory(o => !o)}>
                        {openHistory ? <FiChevronDown size={10} /> : <FiChevronRight size={10} />}
                        <span className={styles.title}>{t('assistant:planInsights.history')}</span>
                    </button>

                    {openHistory && (<>
                    {!history && !candidates && (
                        <button type="button" className={styles.action} disabled={histBusy} onClick={() => loadHistory(null)}>
                            <FiClock size={10} /> {histBusy ? t('assistant:planInsights.looking') : t('assistant:planInsights.lookUp')}
                        </button>
                    )}

                    {/* 후보가 여럿이면 **사람이 고른다** */}
                    {candidates && candidates.length > 0 && (
                        <>
                            <div className={styles.note}>{t('assistant:planInsights.pickOne')}</div>
                            {candidates.map(c => (
                                <button key={c.sqlId} type="button" className={styles.candidate} onClick={() => loadHistory(c.sqlId)}>
                                    <code>{c.sqlId}</code>
                                    <span className={styles.candMeta}>
                                        {c.executions != null ? t('assistant:planInsights.execs', { n: c.executions }) : ''}
                                        {c.lastActive ? ` · ${c.lastActive}` : ''}
                                    </span>
                                    <span className={styles.candHead}>{c.sqlHead}</span>
                                </button>
                            ))}
                        </>
                    )}

                    {histError && <div className={styles.note}>{histError}</div>}

                    {history && (
                        <div className={styles.hist}>
                            <div className={styles.histLine}>
                                <code>{history.sqlId}</code>
                                {history.planChanged && (
                                    // 어제까지 빠르던 게 오늘 느리면 대개 이것이다 — 앞세운다
                                    <span className={styles.warn}>
                                        <FiAlertTriangle size={9} /> {t('assistant:planInsights.planChanged', { n: (history.planHashValues || []).length })}
                                    </span>
                                )}
                            </div>

                            {history.awr && history.awr.length > 0 && (
                                <table className={styles.histTable}>
                                    <thead>
                                        <tr>
                                            <th>{t('assistant:planInsights.when')}</th>
                                            <th>{t('assistant:planInsights.execs2')}</th>
                                            <th>{t('assistant:planInsights.perExec')}</th>
                                            <th>{t('assistant:planInsights.buffers')}</th>
                                            <th>{t('assistant:planInsights.planHash')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.awr.slice(0, 12).map(r => (
                                            <tr key={`${r.snapId}-${r.instanceNumber}`}>
                                                <td>{r.beginTime}</td>
                                                <td>{r.executions}</td>
                                                <td>{r.elapsedPerExecSec != null ? `${r.elapsedPerExecSec}s` : '-'}</td>
                                                <td>{r.bufferGets != null ? r.bufferGets.toLocaleString() : '-'}</td>
                                                <td>{r.planHashValue}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {history.ash && history.ash.length > 0 && (
                                <div className={styles.events}>
                                    {history.ash.slice(0, 6).map((r, i) => (
                                        <div key={`${r.minute}-${r.event}-${i}`} className={styles.event}>
                                            <span>{r.event}</span>
                                            <span className={styles.candMeta}>{t('assistant:planInsights.samples', { n: r.samples })}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* **못 읽은 것과 없는 것을 구분해 말한다.** 뭉뚱그리면 "안 돌았구나" 로 읽힌다 */}
                            {(history.sources || []).filter(s => !s.ok || s.reason).map(s => (
                                <div key={s.name} className={styles.note} title={s.detail || ''}>
                                    {t(`assistant:planInsights.src.${s.name}`)}: {t(`assistant:planInsights.reason.${REASON_KEY[s.reason] || 'error'}`)}
                                </div>
                            ))}
                        </div>
                    )}
                    </>)}
                </div>
            )}
        </div>
    );
}

PlanInsights.propTypes = {
    planText: PropTypes.string,
    connectionId: PropTypes.string,
    sqlText: PropTypes.string,
};

export default PlanInsights;
